/**
 * Broker Store (Unified Multi-Broker State Management)
 *
 * Single source of truth for all broker data including:
 * - Broker connections and status
 * - Accounts, positions, orders, balances per broker
 * - Active broker for order routing
 * - Copy trading configuration
 *
 * All UI components should use this store instead of liveTradingStore.
 * Communication flows: UI -> brokerStore -> wsService -> Backend -> Tradovate Service
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { useMemo } from 'react';
import { wsService } from '../services/WebSocketService';
import { useToastStore } from './toastStore';
import { useMarketStore } from './marketStore';
import { recordingService } from '../services/RecordingService';
import { DEFAULT_BROKER_ID } from '../config/ports';
import { filterFuturesBrokers, isFuturesBroker, isHiddenBrokerId } from './brokerVisibility';
import { checkRestrainOrder, normalizeRestrainSymbol, restrainSymbolForPosition } from './restrainStore';
import { contractSymbolFor, rememberContractSymbol } from './contractSymbols';

// Track pending order modifications for toast feedback
interface PendingModification {
  orderId: number;
  oldPrice?: number;
  newPrice?: number;
  isStopPrice?: boolean;
  oldQty?: number;
  newQty?: number;
  orderType?: string; // 'Limit', 'Stop', etc.
  timestamp: number;
}
const pendingModifications = new Map<number, PendingModification>();
const PENDING_MOD_TIMEOUT_MS = 30000; // Clean up after 30s


// ============================================================================
// POSITION OPEN-TIME TRACKING (for "time in position" timer)
// ============================================================================
// Map of symbol -> epoch ms when the position FIRST opened (0 -> non-zero).
// Persisted to localStorage so the timer survives page reloads. Anchored to
// the first open; re-adds/scaling do not reset it, direction flips reset it,
// and a close clears it.
const POSITION_OPENED_AT_KEY = 'utt:positionOpenedAt';

// Tolerance for clock skew between this machine and the broker's timestamps.
// Beyond it, an "open time" in the future is not skew, it is a bad value.
const OPENED_AT_FUTURE_TOLERANCE_MS = 60_000;

/**
 * Accept an open time only if it could actually be one.
 *
 * The timer renders whatever lands here straight onto a live trading screen, so
 * a value that cannot be true must be refused rather than displayed: a future
 * anchor counts backwards, and a non-finite one renders as NaN. Refusing falls
 * back to Date.now(), which is late but never misleading.
 */
const readOpenedAt = (value: unknown): number | undefined => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  if (value > Date.now() + OPENED_AT_FUTURE_TOLERANCE_MS) return undefined;
  return value;
};

const loadPositionOpenedAt = (): Record<string, number> => {
  try {
    const raw = localStorage.getItem(POSITION_OPENED_AT_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    // Persisted values get the same scrutiny as server ones — they outlive the
    // tab that wrote them and nothing else revalidates them.
    const clean: Record<string, number> = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      const openedAt = readOpenedAt(value);
      if (openedAt !== undefined) clean[key] = openedAt;
    }
    return clean;
  } catch {
    return {};
  }
};

const savePositionOpenedAt = (map: Record<string, number>): void => {
  try {
    localStorage.setItem(POSITION_OPENED_AT_KEY, JSON.stringify(map));
  } catch {
    // ignore quota / unavailable localStorage
  }
};

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface BrokerAccount {
  id: number;
  name: string;
  userId?: number;
  accountType?: string;
  enabled?: boolean; // Whether this account is enabled for trading/display
}

export interface BrokerPosition {
  id: number;
  brokerId: string;
  accountId: number;
  contractId: number;
  symbol?: string;
  netPos: number;
  netPrice: number;
  bought?: number;
  boughtValue?: number;
  sold?: number;
  soldValue?: number;
  openPnL?: number;
  realizedPnL?: number;
  timestamp: string;
  // Exact entry time of the current run (epoch ms), derived server-side from
  // fill history. When present, it overrides the Date.now() open-time fallback.
  openedAt?: number;
  // For position change tracking
  prevPos?: number;
  prevPrice?: number;
}

export interface BrokerOrder {
  id: number;
  brokerId: string;
  accountId: number;
  contractId: number;
  symbol?: string;
  action: 'Buy' | 'Sell';
  orderQty: number;
  filledQty?: number;
  ordType: string;
  price?: number;
  stopPrice?: number;
  ordStatus: string;
  ocoId?: number;
  parentId?: number;
  timestamp: string;
}

export interface BrokerBalance {
  brokerId: string;
  accountId: number;
  cashBalance: number;
  openPnL: number;
  realizedPnL: number;
  totalPnL: number;
  netLiq: number;
  marginUsed: number;
  availableMargin?: number;
  timestamp: number;
}

export interface BrokerState {
  // Identity
  id: string;
  name: string;

  // Connection
  status: 'disconnected' | 'connecting' | 'connected' | 'error';
  connectedAt?: number;
  error?: string;

  // Settings (persisted to Redis)
  enabled: boolean;
  autoConnect: boolean;

  // Account Data
  accounts: BrokerAccount[];
  selectedAccountId: number | null;
  isDemo: boolean;

  // Trading Data (per selected account)
  positions: BrokerPosition[];
  orders: BrokerOrder[];
  balance: BrokerBalance | null;

  /**
   * When this broker last delivered a positions SNAPSHOT, or `undefined` if it
   * never has (or if the last one has since been invalidated by a disconnect).
   *
   * `positions: []` alone cannot answer "is this account flat?" — a broker is
   * constructed with an empty array and `broker:positions` replaces the whole
   * thing, so empty means either "flat" or "nothing has arrived yet" and there
   * is no way to tell which. Anything that has to distinguish the two needs
   * this. Flat is the NORMAL state, so treating empty as unknown silently
   * disables whatever depends on it — see the trade-marker anchor in ChartView.
   *
   * Set ONLY by `updateBrokerPositions`, which is the only path a real snapshot
   * arrives on (`broker:list` carries no trading data). Cleared wherever
   * `positions` is reset to `[]` as an invalidation — connect and disconnect —
   * so the flag can never outlive the data it vouches for.
   */
  positionsLoadedAt?: number;

  // Per-account balances (accountId -> balance)
  accountBalances?: Record<number, BrokerBalance>;

  // Metadata
  lastUpdated: number;
}

// Aggregated position across all brokers for same symbol
export interface AggregatedPosition {
  symbol: string;
  totalNetPos: number;
  avgEntryPrice: number;
  totalOpenPnL: number;
  brokerPositions: BrokerPosition[];
}

// Aggregated balance across all brokers
export interface AggregatedBalance {
  netLiq: number;
  openPnL: number;
  realizedPnL: number;
  totalPnL: number;
  cashBalance: number;
  marginUsed: number;
  brokerBalances: BrokerBalance[];
}

// Copy trading types
export type CopyDirectionMode = 'same' | 'inverse';
export type CopySizeMode = 'mirror' | 'scaled' | 'fixed';

export interface FollowerConfig {
  brokerId: string;
  accountId: number;
  enabled: boolean;
  directionMode: CopyDirectionMode;
  sizeMode: CopySizeMode;
  sizeMultiplier?: number;
  fixedSize?: number;
  maxPositionSize?: number;
  copyBrackets: boolean;
  copyFlattens: boolean;
}

export interface CopyTradingConfig {
  enabled: boolean;
  master: {
    brokerId: string;
    accountId: number;
  };
  followers: FollowerConfig[];
}

export interface CopyTradeEvent {
  id: string;
  timestamp: number;
  type: 'order' | 'flatten';
  masterOrder?: {
    brokerId: string;
    accountId: number;
    orderId?: number;
    action: 'Buy' | 'Sell';
    symbol: string;
    quantity: number;
    fillPrice?: number;
  };
  followerResults: Array<{
    brokerId: string;
    accountId: number;
    status: 'pending' | 'success' | 'failed';
    orderId?: number;
    action?: 'Buy' | 'Sell';
    quantity?: number;
    fillPrice?: number;
    error?: string;
    latencyMs?: number;
  }>;
}

export interface CopyTradeActivity {
  id: string;           // uuid
  ts: string;           // ISO timestamp
  event: string;        // Event type from CopyTradingLogger (e.g., 'order:copied', 'flatten:detected')
  level: 'info' | 'warn' | 'error' | 'debug';
  msg: string;          // Human-readable message
  symbol?: string;
  master?: string;
  follower?: string;
  action?: string;      // Buy/Sell
  qty?: number;
  duration_ms?: number;
  error?: string;
}

// Result type for async operations
interface Result {
  success: boolean;
  error?: string;
  /**
   * broker:place-order on a partial exit: tradovate-service found TP/SL legs
   * on the position and sized them to what is left BEFORE the exit went out
   * (ExitOnlyGate PARTIAL EXITS WITH TP/SL RESTING) - do not re-place them.
   */
  protectiveLegs?: { handled: true; resized: Array<{ orderId: number | string; from: number; to: number }> };
  /** What happened to TP/SL legs shrunk for an exit that then failed. */
  protectiveLegsNote?: string;
}

// ============================================================================
// STORE INTERFACE
// ============================================================================

interface BrokerStore {
  // ============================================================================
  // STATE
  // ============================================================================

  brokers: BrokerState[];
  activeBrokerId: string | null;
  isLoading: boolean;
  error: string | null;

  // RMS (Risk Management System) state
  tradingLocked: boolean;
  tradingLockedAt: string | null;
  tradingLockedReason: string | null;

  // Copy trading state
  copyTradingConfig: CopyTradingConfig | null;
  copyTradingEnabled: boolean;
  copyTradingHistory: CopyTradeEvent[];
  copyTradeActivities: CopyTradeActivity[];  // Activity log (max 200 entries)

  // Position open-time tracking (symbol -> epoch ms when position first opened)
  // Drives the "time in position" timer. Persisted to localStorage.
  positionOpenedAt: Record<string, number>;

  // ============================================================================
  // GETTERS (computed)
  // ============================================================================

  getActiveBroker: () => BrokerState | null;
  getConnectedBrokers: () => BrokerState[];
  getBroker: (brokerId: string) => BrokerState | undefined;
  getAllPositions: () => BrokerPosition[];
  getAllOrders: () => BrokerOrder[];
  getAggregatedPositions: () => AggregatedPosition[];
  getAggregatedBalance: () => AggregatedBalance;
  getAllAccounts: () => Array<BrokerAccount & { brokerId: string; brokerName: string }>;

  // Hedge copy mode: returns true if copy trading is enabled with an inverse/hedge follower
  isHedgeCopyModeActive: () => boolean;

  // ============================================================================
  // BROKER STATE UPDATES (called by WebSocket handlers)
  // ============================================================================

  setBrokers: (brokers: BrokerState[]) => void;
  updateBroker: (brokerId: string, partial: Partial<BrokerState>) => void;
  updateBrokerStatus: (brokerId: string, status: BrokerState['status'], error?: string) => void;
  setBrokerAccounts: (brokerId: string, accounts: BrokerAccount[]) => void;
  updateBrokerPositions: (brokerId: string, positions: any[]) => void;
  updateBrokerOrders: (brokerId: string, orders: any[]) => void;
  updateBrokerBalance: (brokerId: string, balance: any) => void;
  updateBrokerAccountBalances: (brokerId: string, accountBalances: Record<number, BrokerBalance>) => void;

  // ============================================================================
  // BROKER ACTIONS
  // ============================================================================

  setActiveBroker: (brokerId: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  fetchBrokers: () => void;
  connectBroker: (brokerId: string) => Promise<Result>;
  disconnectBroker: (brokerId: string) => Promise<Result>;
  saveBrokerCredentials: (brokerId: string, username: string, password: string) => Promise<Result>;
  selectBrokerAccount: (brokerId: string, accountId: number) => void;
  setBrokerEnabled: (brokerId: string, enabled: boolean) => Promise<Result>;
  setBrokerAutoConnect: (brokerId: string, autoConnect: boolean) => Promise<Result>;
  setAccountEnabled: (brokerId: string, accountId: number, enabled: boolean) => Promise<Result | undefined>;

  // ============================================================================
  // TRADING ACTIONS (routed to active broker)
  // ============================================================================

  placeOrder: (params: {
    action: 'Buy' | 'Sell';
    symbol: string;
    quantity: number;
    orderType?: 'Market' | 'Limit' | 'Stop' | 'StopLimit';
    price?: number;
    stopPrice?: number;
    brokerId?: string;
    accountId?: number;
  }) => Promise<Result>;

  placeBracketOrder: (params: {
    action: 'Buy' | 'Sell';
    symbol: string;
    quantity: number;
    orderType?: 'Market' | 'Limit' | 'Stop' | 'StopLimit';
    price?: number;
    stopPrice?: number;
    profitTargetPoints?: number;
    stopLossPoints?: number;
    brokerId?: string;
    accountId?: number;
  }) => Promise<Result>;

  modifyOrder: (orderId: number, newPrice: number, isStopPrice: boolean, brokerId?: string, accountId?: number, skipDebounce?: boolean) => void;
  modifyOrderQuantity: (orderId: number, newQuantity: number, brokerId?: string, accountId?: number) => void;
  cancelOrder: (orderId: number, brokerId?: string, accountId?: number) => void;

  // Split an order into two orders with different quantities (one at original price, one offset by 1 tick)
  splitOrder: (params: {
    orderId: number;
    qty1: number;
    qty2: number;
    price1: number;
    price2: number;
    brokerId?: string;
    accountId?: number;
  }) => Promise<Result>;

  // Replace bracket orders (cancel TP/SL and place new ones with updated qty)
  replaceBracket: (params: {
    tpOrderId?: number;
    slOrderId?: number;
    symbol: string;
    action: 'Buy' | 'Sell';
    newQuantity: number;
    tpPrice?: number;
    slPrice?: number;
    brokerId?: string;
    accountId?: number;
  }) => Promise<Result>;

  flattenPosition: (symbol: string, brokerId?: string, accountId?: number) => Promise<Result>;
  flattenAllPositions: (symbol: string) => Promise<Result>;

  // Data fetching
  fetchPositions: (brokerId?: string) => void;
  fetchOrders: (brokerId?: string) => void;
  fetchAccountBalance: (brokerId?: string) => void;

  // ============================================================================
  // RMS ACTIONS
  // ============================================================================

  setTradingLocked: (locked: boolean, lockedAt?: string | null, reason?: string | null) => void;
  setTradingLock: (locked: boolean, reason?: string) => Promise<Result>;
  fetchRMSStatus: () => void;

  // ============================================================================
  // COPY TRADING ACTIONS
  // ============================================================================

  setCopyTradingConfig: (config: CopyTradingConfig | null) => void;
  setCopyTradingEnabled: (enabled: boolean) => void;
  addCopyTradeEvent: (event: CopyTradeEvent) => void;
  setCopyTradingHistory: (history: CopyTradeEvent[]) => void;
  addCopyTradeActivity: (activity: CopyTradeActivity) => void;
  clearCopyTradeActivities: () => void;

  fetchCopyTradingStatus: () => void;
  updateCopyTradingConfig: (config: CopyTradingConfig) => Promise<Result>;
  enableCopyTrading: () => Promise<Result>;
  disableCopyTrading: () => Promise<Result>;
  fetchCopyTradingHistory: () => void;
}

// Type alias for selector functions
type BrokerStoreState = BrokerStore;

// ============================================================================
// CONSTANTS
// ============================================================================

const REQUEST_TIMEOUT = 10000;
const CONNECT_TIMEOUT = 60000;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Client mirror of the server's restrain gate (tradovate-service is the
 * authority and refuses with RESTRAIN_ACTIVE anyway): refuse locally, with the
 * same message, an order that would change a restrained position's size, so
 * the user isn't surprised by a round-trip refusal.
 */
function restrainRefusal(
  broker: BrokerState,
  accountId: number,
  symbol: string,
  action: 'Buy' | 'Sell',
  quantity: number,
): string | null {
  const sym = normalizeRestrainSymbol(symbol);
  // The position's contract may not be named yet (only a contractId): it is
  // then resolved through the contract cache, or taken to be the order's.
  const pos = broker.positions.find(p => {
    if (p.netPos === 0 || String(p.accountId) !== String(accountId)) return false;
    const own = restrainSymbolForPosition(p, null);
    return !own || own === sym;
  });
  if (!pos) return null;
  return checkRestrainOrder({
    brokerId: pos.brokerId || broker.id,
    accountId,
    symbol: pos.symbol,
    contractId: pos.contractId,
    fallbackSymbol: symbol,
    netPos: pos.netPos,
    action,
    quantity,
  });
}

function createTimeoutPromise<T>(
  emitEvent: string,
  emitData: any,
  successEvent: string,
  errorEvent: string,
  timeout: number,
  matchFn?: (data: any) => boolean
): Promise<T> {
  return new Promise((resolve) => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let resolved = false;
    let cleanedUp = false;

    const cleanup = () => {
      // Prevent double cleanup
      if (cleanedUp) return;
      cleanedUp = true;

      try {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        // Wrap off() calls in try-catch to prevent errors from leaking
        try {
          wsService.off(successEvent, handleSuccess);
        } catch (e) {
          console.warn('[createTimeoutPromise] Error removing success handler:', e);
        }
        try {
          wsService.off(errorEvent, handleError);
        } catch (e) {
          console.warn('[createTimeoutPromise] Error removing error handler:', e);
        }
      } catch (e) {
        console.error('[createTimeoutPromise] Cleanup error:', e);
      }
    };

    const handleSuccess = (data: any) => {
      if (matchFn && !matchFn(data)) return;
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve({ success: true, ...data } as T);
    };

    const handleError = (data: any) => {
      if (matchFn && !matchFn(data)) return;
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve({ success: false, error: data.error || data.message } as T);
    };

    wsService.on(successEvent, handleSuccess);
    wsService.on(errorEvent, handleError);

    timeoutId = setTimeout(() => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve({ success: false, error: 'Request timed out' } as T);
    }, timeout);

    try {
      wsService.emit(emitEvent, emitData);
    } catch (e) {
      if (resolved) return;
      resolved = true;
      cleanup();
      console.error('[createTimeoutPromise] Emit failed:', e);
      resolve({ success: false, error: e instanceof Error ? e.message : 'Emit failed' } as T);
    }
  });
}

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

export const useBrokerStore = create<BrokerStore>()(
  subscribeWithSelector((set, get) => ({
  // ============================================================================
  // INITIAL STATE
  // ============================================================================

  brokers: [],
  activeBrokerId: null,
  isLoading: false,
  error: null,

  tradingLocked: false,
  tradingLockedAt: null,
  tradingLockedReason: null,

  copyTradingConfig: null,
  copyTradingEnabled: false,
  copyTradingHistory: [],
  copyTradeActivities: [],

  // Load persisted position open-times so the timer survives page reloads
  positionOpenedAt: loadPositionOpenedAt(),

  // ============================================================================
  // GETTERS
  // ============================================================================

  getActiveBroker: () => {
    const { brokers, activeBrokerId } = get();
    return brokers.find(b => b.id === activeBrokerId) || null;
  },

  getConnectedBrokers: () => {
    return get().brokers.filter(b => b.status === 'connected');
  },

  getBroker: (brokerId: string) => {
    return get().brokers.find(b => b.id === brokerId);
  },

  getAllPositions: () => {
    const { brokers } = get();
    const positions: BrokerPosition[] = [];
    brokers.forEach(broker => {
      if (broker.status === 'connected') {
        positions.push(...broker.positions);
      }
    });
    return positions;
  },

  getAllOrders: () => {
    const { brokers } = get();
    const orders: BrokerOrder[] = [];
    brokers.forEach(broker => {
      if (broker.status === 'connected') {
        orders.push(...broker.orders);
      }
    });
    return orders;
  },

  getAggregatedPositions: () => {
    const positions = get().getAllPositions();
    const bySymbol = new Map<string, BrokerPosition[]>();

    positions.forEach(pos => {
      const symbol = pos.symbol || `Contract#${pos.contractId}`;
      const existing = bySymbol.get(symbol) || [];
      existing.push(pos);
      bySymbol.set(symbol, existing);
    });

    return Array.from(bySymbol.entries()).map(([symbol, brokerPositions]) => {
      const totalNetPos = brokerPositions.reduce((sum, p) => sum + p.netPos, 0);
      const totalValue = brokerPositions.reduce((sum, p) => sum + Math.abs(p.netPos) * p.netPrice, 0);
      const totalQty = brokerPositions.reduce((sum, p) => sum + Math.abs(p.netPos), 0);

      return {
        symbol,
        totalNetPos,
        avgEntryPrice: totalQty > 0 ? totalValue / totalQty : 0,
        totalOpenPnL: brokerPositions.reduce((sum, p) => sum + (p.openPnL || 0), 0),
        brokerPositions,
      };
    });
  },

  getAggregatedBalance: () => {
    const { brokers } = get();
    const brokerBalances: BrokerBalance[] = [];

    brokers.forEach(broker => {
      if (broker.status === 'connected' && broker.balance) {
        brokerBalances.push(broker.balance);
      }
    });

    const netLiq = brokerBalances.reduce((sum, b) => sum + b.netLiq, 0);
    const openPnL = brokerBalances.reduce((sum, b) => sum + b.openPnL, 0);
    const realizedPnL = brokerBalances.reduce((sum, b) => sum + b.realizedPnL, 0);
    const cashBalance = brokerBalances.reduce((sum, b) => sum + b.cashBalance, 0);
    const marginUsed = brokerBalances.reduce((sum, b) => sum + (b.marginUsed || 0), 0);

    return {
      netLiq,
      openPnL,
      realizedPnL,
      totalPnL: openPnL + realizedPnL,
      cashBalance,
      marginUsed,
      brokerBalances,
    };
  },

  getAllAccounts: () => {
    const { brokers } = get();
    const accounts: Array<BrokerAccount & { brokerId: string; brokerName: string }> = [];

    brokers.forEach(broker => {
      if (broker.status === 'connected') {
        broker.accounts.forEach(account => {
          accounts.push({
            ...account,
            brokerId: broker.id,
            brokerName: broker.name,
          });
        });
      }
    });

    return accounts;
  },

  // Hedge copy mode: returns true if copy trading is enabled with an inverse/hedge follower
  isHedgeCopyModeActive: () => {
    const { copyTradingEnabled, copyTradingConfig } = get();
    if (!copyTradingEnabled || !copyTradingConfig) return false;

    // Check if any enabled follower has inverse direction mode
    const hasHedgeFollower = copyTradingConfig.followers.some(
      f => f.enabled && f.directionMode === 'inverse'
    );

    return hasHedgeFollower;
  },

  // ============================================================================
  // BROKER STATE UPDATES
  // ============================================================================

  setBrokers: (incoming) => {
    // Futures only: a non-Tradovate broker (and any global-default account on
    // it) never enters the store, so it can't be shown, selected or traded.
    const brokers = filterFuturesBrokers(incoming);

    // Get existing brokers to preserve trading data that is sent separately
    // (positions, orders, balance, accountBalances come via separate events)
    const existingBrokers = get().brokers;
    const existingDataMap = new Map<string, {
      positions: BrokerPosition[];
      positionsLoadedAt?: number;
      orders: BrokerOrder[];
      balance: BrokerBalance | null;
      accountBalances?: Record<number, BrokerBalance>;
    }>();
    existingBrokers.forEach(b => {
      existingDataMap.set(b.id, {
        positions: b.positions || [],
        positionsLoadedAt: b.positionsLoadedAt,
        orders: b.orders || [],
        balance: b.balance || null,
        accountBalances: b.accountBalances,
      });
    });

    // Ensure defaults and initialize trading data arrays
    // CRITICAL: Preserve existing positions/orders/balance if incoming broker doesn't have them
    // This prevents data loss when broker:list is called (which doesn't include trading data)
    const normalizedBrokers = brokers.map(b => {
      const existing = existingDataMap.get(b.id);

      return {
        ...b,
        enabled: b.enabled ?? true,
        autoConnect: b.autoConnect ?? false,
        accounts: b.accounts || [], // Backend sends accounts with enabled state from Redis
        // Preserve existing trading data if not provided in update
        positions: (b.positions && b.positions.length > 0) ? b.positions : (existing?.positions || []),
        // Carried across explicitly. `broker:list` has no trading data on it
        // (the service's BrokerState carries accounts and balances, not
        // positions), so this rebuild would otherwise DROP the flag on every
        // list refresh — and one is requested on every socket connect. Losing
        // it would put the anchor back to "unknown" at random intervals.
        positionsLoadedAt: existing?.positionsLoadedAt,
        orders: (b.orders && b.orders.length > 0) ? b.orders : (existing?.orders || []),
        balance: b.balance || existing?.balance || null,
        accountBalances: b.accountBalances || existing?.accountBalances,
        lastUpdated: b.lastUpdated || Date.now(),
      };
    });

    set({ brokers: normalizedBrokers, isLoading: false });

    // Set default active broker if not set
    // IMPORTANT: Use the broker with selectedAccountId (global default from backend)
    const state = get();
    if (!state.activeBrokerId && normalizedBrokers.length > 0) {
      // Priority 1: Broker with selected account (global default from Redis)
      const brokerWithSelection = normalizedBrokers.find(b => b.selectedAccountId && b.status === 'connected');
      // Priority 2: DEFAULT_BROKER_ID if connected
      const defaultBroker = normalizedBrokers.find(b => b.id === DEFAULT_BROKER_ID && b.status === 'connected');
      // Priority 3: First connected broker
      const firstConnected = normalizedBrokers.find(b => b.status === 'connected');
      // Priority 4: First broker in list
      const chosen = brokerWithSelection?.id || defaultBroker?.id || firstConnected?.id || normalizedBrokers[0].id;

      console.log('[BROKER STORE] Setting activeBrokerId:', {
        chosen,
        brokerWithSelection: brokerWithSelection ? `${brokerWithSelection.id} (account: ${brokerWithSelection.selectedAccountId})` : 'none',
        defaultBroker: defaultBroker?.id,
        firstConnected: firstConnected?.id,
      });

      set({ activeBrokerId: chosen });
    }
  },

  updateBroker: (brokerId, partial) => {
    if (isHiddenBrokerId(brokerId)) return;
    set(state => ({
      brokers: state.brokers.map(b =>
        b.id === brokerId ? { ...b, ...partial, lastUpdated: Date.now() } : b
      ),
    }));
  },

  updateBrokerStatus: (brokerId, status, error) => {
    if (isHiddenBrokerId(brokerId)) return;
    set(state => ({
      brokers: state.brokers.map(b =>
        b.id === brokerId ? { ...b, status, error, lastUpdated: Date.now() } : b
      ),
    }));
  },

  setBrokerAccounts: (brokerId, accounts) => {
    if (isHiddenBrokerId(brokerId)) return;
    // Backend sends accounts with enabled state already applied from Redis
    // No need to apply localStorage enabled state here

    // Update broker accounts (backend handles account selection persistence via Redis)
    set(state => ({
      brokers: state.brokers.map(b =>
        b.id === brokerId
          ? {
              ...b,
              accounts: accounts, // Use accounts as-is from backend
              // Keep existing selectedAccountId (backend will send broker:account-selected if it needs to change)
              lastUpdated: Date.now()
            }
          : b
      ),
    }));
  },

  updateBrokerPositions: (brokerId, positions) => {
    if (isHiddenBrokerId(brokerId)) return;
    const normalizedPositions: BrokerPosition[] = positions.map(p => ({
      ...p,
      brokerId,
    }));

    // Update global contract symbol cache from positions
    normalizedPositions.forEach(p => rememberContractSymbol(p.contractId, p.symbol));

    // Get old positions to detect when position goes to zero
    const broker = get().getBroker(brokerId);
    const oldPositions = broker?.positions || [];
    // Also get current orders BEFORE state update (orders come from separate event)
    const currentOrders = broker?.orders || [];

    console.log(`[BROKER STORE] updateBrokerPositions: old=${oldPositions.length} positions, new=${normalizedPositions.length} positions, orders=${currentOrders.length}`);

    set(state => ({
      brokers: state.brokers.map(b =>
        b.id === brokerId
          ? {
              ...b,
              positions: normalizedPositions,
              // A snapshot ARRIVED. Stamped even when the array is empty —
              // that is the whole point: it is what makes "flat" legible as
              // flat rather than as "we have not heard yet".
              positionsLoadedAt: Date.now(),
              lastUpdated: Date.now(),
            }
          : b
      ),
    }));

    // === TRADE RECORDING INTEGRATION ===
    // Detect trade opens (0 -> non-zero) and closes (non-zero -> 0)
    const currentSymbol = useMarketStore.getState().symbol;

    // Working copy of position open-times; mutated below and persisted once at the end
    const openedAtMap: Record<string, number> = { ...get().positionOpenedAt };
    let openedAtChanged = false;

    // Check for NEW positions (trade open)
    for (const newPos of normalizedPositions) {
      const newNetPos = newPos.netPos || 0;
      if (newNetPos === 0) continue; // Not a real position

      // Find matching old position
      let oldPos = oldPositions.find(p => p.id === newPos.id);
      if (!oldPos && newPos.contractId) {
        oldPos = oldPositions.find(p => p.contractId === newPos.contractId);
      }
      const oldNetPos = oldPos?.netPos || 0;

      // Same key used by recordingService so the overlay can read by symbol
      const openKey = newPos.symbol || currentSymbol;

      // Server-authoritative open time (exact entry from fill history). When the
      // backend supplies a usable openedAt, it always wins over Date.now().
      const serverOpenedAt = readOpenedAt(newPos.openedAt);

      // Trade OPEN: went from 0 to non-zero
      if (oldNetPos === 0 && newNetPos !== 0) {
        console.log(`[BROKER STORE] 🎬 Trade OPEN detected: ${newPos.symbol} netPos=${newNetPos}`);
        recordingService.onTradeOpen(newPos.symbol || currentSymbol, Date.now());
        // Prefer the exact server time; otherwise anchor to first open (no reset).
        if (serverOpenedAt !== undefined) {
          if (openedAtMap[openKey] !== serverOpenedAt) {
            openedAtMap[openKey] = serverOpenedAt;
            openedAtChanged = true;
          }
        } else if (!openedAtMap[openKey]) {
          openedAtMap[openKey] = Date.now();
          openedAtChanged = true;
        }
      }
      // Position FLIP: changed direction (e.g., long to short or vice versa)
      // This is effectively a close + open, so we trigger both
      else if (oldNetPos !== 0 && newNetPos !== 0 && Math.sign(oldNetPos) !== Math.sign(newNetPos)) {
        console.log(`[BROKER STORE] 🎬 Position FLIP detected: ${newPos.symbol} ${oldNetPos} -> ${newNetPos}`);
        // Close the old position recording
        recordingService.onTradeClose(newPos.symbol || currentSymbol, Date.now());
        // Start new recording for the flipped position (after small delay to ensure close completes)
        setTimeout(() => {
          recordingService.onTradeOpen(newPos.symbol || currentSymbol, Date.now());
        }, 100);
        // Flip is a new position - reset the timer anchor (prefer exact server time).
        openedAtMap[openKey] = serverOpenedAt !== undefined ? serverOpenedAt : Date.now();
        openedAtChanged = true;
      }
    }

    // Check for positions that went from non-zero to zero - cancel orphan orders
    // This happens when TP/SL fills and they're no longer OCO-linked after replaceBracket
    for (const oldPos of oldPositions) {
      const oldNetPos = oldPos.netPos || 0;
      if (oldNetPos === 0) continue; // Was already flat

      // Find matching new position - check by id first, then by contractId
      let newPos = normalizedPositions.find(p => p.id === oldPos.id);
      if (!newPos && oldPos.contractId) {
        newPos = normalizedPositions.find(p => p.contractId === oldPos.contractId);
      }
      const newNetPos = newPos?.netPos || 0;

      console.log(`[BROKER STORE] Position change check: id=${oldPos.id}, old=${oldNetPos}, new=${newNetPos}`);

      if (newNetPos === 0 && oldNetPos !== 0) {
        // Position went to zero - cancel any remaining working orders
        console.log(`[BROKER STORE] Position ${oldPos.id} went to zero (was ${oldNetPos}), checking for orphan orders...`);

        // Trade CLOSE: position flattened
        console.log(`[BROKER STORE] 🎬 Trade CLOSE detected: ${oldPos.symbol} flattened`);
        recordingService.onTradeClose(oldPos.symbol || currentSymbol, Date.now());

        // Clear the timer anchor for the closed position
        const closeKey = oldPos.symbol || currentSymbol;
        if (openedAtMap[closeKey]) {
          delete openedAtMap[closeKey];
          openedAtChanged = true;
        }

        // Use current orders (captured BEFORE state update) since order updates come separately
        // Only cancel orders for the same account as the position that closed
        const workingOrders = currentOrders.filter(o =>
          (o.ordStatus === 'Working' || o.ordStatus === 'Accepted' || o.ordStatus === 'Pending') &&
          (o.price || o.stopPrice) && // Has a resting price
          o.accountId === oldPos.accountId // Same account as the closed position
        );

        console.log(`[BROKER STORE] Found ${workingOrders.length} working orders:`, workingOrders.map(o => ({
          id: o.id, type: o.ordType, status: o.ordStatus, price: o.price, stopPrice: o.stopPrice
        })));

        // Cancel all working orders (they're likely orphaned TP/SL)
        for (const order of workingOrders) {
          console.log(`[BROKER STORE] Canceling orphan order ${order.id} (${order.ordType} @ ${order.price || order.stopPrice})`);
          get().cancelOrder(order.id, brokerId);
        }

        // Only show toast if this is the active broker AND position belongs to currently selected account
        const isActiveBroker = get().activeBrokerId === brokerId;
        const isSelectedAccount = broker?.selectedAccountId && oldPos.accountId === broker?.selectedAccountId;
        if (workingOrders.length > 0 && isActiveBroker && isSelectedAccount) {
          useToastStore.getState().addToast({
            type: 'info',
            message: `Position closed - canceled ${workingOrders.length} orphan order(s)`,
            duration: 3000
          });
        } else if (workingOrders.length > 0) {
          console.log(`[BROKER STORE] Skipping toast - activeBroker=${isActiveBroker}, account ${oldPos.accountId} vs selected ${broker?.selectedAccountId}`);
        } else {
          console.log(`[BROKER STORE] No working orders found to cancel`);
        }
      } else if (newNetPos !== 0 && Math.abs(newNetPos) < Math.abs(oldNetPos)) {
        // PARTIAL FILL: Position decreased but didn't go to zero
        // Check if we have more TP/SL orders than position qty and cancel excess
        console.log(`[BROKER STORE] Position ${oldPos.id} partially filled: ${oldNetPos} -> ${newNetPos}`);

        const isLong = newNetPos > 0;
        const posQty = Math.abs(newNetPos);
        const entryPrice = newPos?.netPrice || 0;

        // Find all TP orders (Limit orders with opposite action)
        const tpOrders = currentOrders.filter(o =>
          (o.ordStatus === 'Working' || o.ordStatus === 'Accepted' || o.ordStatus === 'Pending') &&
          o.ordType?.toLowerCase().includes('limit') &&
          !o.ordType?.toLowerCase().includes('stop') &&
          o.price &&
          o.accountId === oldPos.accountId &&
          (isLong ? o.action === 'Sell' : o.action === 'Buy')
        );

        // Find all SL orders (Stop orders with opposite action)
        const slOrders = currentOrders.filter(o =>
          (o.ordStatus === 'Working' || o.ordStatus === 'Accepted' || o.ordStatus === 'Pending') &&
          o.ordType?.toLowerCase().includes('stop') &&
          o.stopPrice &&
          o.accountId === oldPos.accountId &&
          (isLong ? o.action === 'Sell' : o.action === 'Buy')
        );

        // Calculate total order quantities
        const totalTpQty = tpOrders.reduce((sum, o) => sum + (o.orderQty || 0), 0);
        const totalSlQty = slOrders.reduce((sum, o) => sum + (o.orderQty || 0), 0);

        console.log(`[BROKER STORE] Position qty: ${posQty}, TP total qty: ${totalTpQty} (${tpOrders.length} orders), SL total qty: ${totalSlQty} (${slOrders.length} orders)`);

        // Cancel excess TP orders (farthest from entry first)
        if (totalTpQty > posQty) {
          const excessTpQty = totalTpQty - posQty;
          console.log(`[BROKER STORE] TP orders exceed position by ${excessTpQty} - canceling excess`);

          // Sort by distance from entry (farthest first)
          const sortedTpOrders = [...tpOrders].sort((a, b) => {
            const distA = Math.abs((a.price || 0) - entryPrice);
            const distB = Math.abs((b.price || 0) - entryPrice);
            return distB - distA; // Farthest first
          });

          let qtyToCancelTp = excessTpQty;
          for (const order of sortedTpOrders) {
            if (qtyToCancelTp <= 0) break;
            console.log(`[BROKER STORE] Canceling excess TP order ${order.id} @ ${order.price} (qty ${order.orderQty})`);
            get().cancelOrder(order.id, brokerId);
            qtyToCancelTp -= (order.orderQty || 0);
          }
        }

        // Cancel excess SL orders (farthest from entry first)
        if (totalSlQty > posQty) {
          const excessSlQty = totalSlQty - posQty;
          console.log(`[BROKER STORE] SL orders exceed position by ${excessSlQty} - canceling excess`);

          // Sort by distance from entry (farthest first)
          const sortedSlOrders = [...slOrders].sort((a, b) => {
            const distA = Math.abs((a.stopPrice || 0) - entryPrice);
            const distB = Math.abs((b.stopPrice || 0) - entryPrice);
            return distB - distA; // Farthest first
          });

          let qtyToCancelSl = excessSlQty;
          for (const order of sortedSlOrders) {
            if (qtyToCancelSl <= 0) break;
            console.log(`[BROKER STORE] Canceling excess SL order ${order.id} @ ${order.stopPrice} (qty ${order.orderQty})`);
            get().cancelOrder(order.id, brokerId);
            qtyToCancelSl -= (order.orderQty || 0);
          }
        }

        // Show toast if we canceled anything
        const isSelectedAccount = broker?.selectedAccountId === null || oldPos.accountId === broker?.selectedAccountId;
        const canceledTpCount = totalTpQty > posQty ? tpOrders.filter((_, i) => i < Math.ceil((totalTpQty - posQty) / (tpOrders[0]?.orderQty || 1))).length : 0;
        const canceledSlCount = totalSlQty > posQty ? slOrders.filter((_, i) => i < Math.ceil((totalSlQty - posQty) / (slOrders[0]?.orderQty || 1))).length : 0;

        if ((canceledTpCount > 0 || canceledSlCount > 0) && isSelectedAccount) {
          const parts = [];
          if (canceledTpCount > 0) parts.push(`${canceledTpCount} TP`);
          if (canceledSlCount > 0) parts.push(`${canceledSlCount} SL`);
          useToastStore.getState().addToast({
            type: 'info',
            message: `Partial fill - canceled ${parts.join(' + ')} excess order(s)`,
            duration: 3000
          });
        }
      }
    }

    // Ensure every currently-open position has a timer anchor.
    // Covers positions opened before this feature existed, opened externally, or
    // present after a page reload (no 0 -> non-zero transition observed).
    // The server's exact openedAt is authoritative and overrides any prior
    // Date.now() fallback for that key; only fall back to Date.now() when the
    // server provides no open time.
    for (const pos of normalizedPositions) {
      if ((pos.netPos || 0) === 0) continue;
      const key = pos.symbol || currentSymbol;
      const serverOpenedAt = readOpenedAt(pos.openedAt);
      if (serverOpenedAt !== undefined) {
        if (openedAtMap[key] !== serverOpenedAt) {
          openedAtMap[key] = serverOpenedAt;
          openedAtChanged = true;
        }
      } else if (!openedAtMap[key]) {
        openedAtMap[key] = Date.now();
        openedAtChanged = true;
      }
    }

    // Persist + publish the updated open-time map so subscribers re-render
    if (openedAtChanged) {
      savePositionOpenedAt(openedAtMap);
      set({ positionOpenedAt: { ...openedAtMap } });
    }
  },

  updateBrokerOrders: (brokerId, orders) => {
    if (isHiddenBrokerId(brokerId)) return;
    // Debug: Log order prices being received
    orders.forEach(o => {
      if (o.ordStatus === 'Working' || o.ordStatus === 'Accepted' || o.ordStatus === 'Pending') {
        console.log(`[BROKER STORE] Order ${o.id} received: price=${o.price}, stopPrice=${o.stopPrice}, status=${o.ordStatus}, symbol=${o.symbol}`);
      }
    });

    // Orders that do name their contract teach the contractId -> symbol cache
    orders.forEach(o => rememberContractSymbol(o.contractId, o.symbol));

    // Get current broker to look up positions for symbol mapping
    const currentBroker = get().brokers.find(b => b.id === brokerId);
    const positions = currentBroker?.positions || [];

    const normalizedOrders: BrokerOrder[] = orders.map(o => {
      // If order doesn't have symbol, try to look it up from positions or global cache
      let symbol = o.symbol;
      if (!symbol && o.contractId) {
        // First check current positions
        const matchingPosition = positions.find(p => p.contractId === o.contractId);
        if (matchingPosition?.symbol) {
          symbol = matchingPosition.symbol;
        } else {
          // Fall back to global contract cache
          symbol = contractSymbolFor(o.contractId);
        }
      }
      return {
        ...o,
        brokerId,
        symbol,
      };
    });

    set(state => ({
      brokers: state.brokers.map(b =>
        b.id === brokerId
          ? { ...b, orders: normalizedOrders, lastUpdated: Date.now() }
          : b
      ),
    }));
  },

  updateBrokerBalance: (brokerId, balance) => {
    if (isHiddenBrokerId(brokerId)) return;
    const normalizedBalance: BrokerBalance = {
      ...balance,
      brokerId,
    };

    set(state => ({
      brokers: state.brokers.map(b =>
        b.id === brokerId
          ? { ...b, balance: normalizedBalance, lastUpdated: Date.now() }
          : b
      ),
    }));
  },

  updateBrokerAccountBalances: (brokerId, accountBalances) => {
    if (isHiddenBrokerId(brokerId)) return;
    // Normalize all balances with brokerId
    const normalized: Record<number, BrokerBalance> = {};
    for (const [accountIdStr, balance] of Object.entries(accountBalances)) {
      const accountId = parseInt(accountIdStr, 10);
      normalized[accountId] = {
        ...balance,
        brokerId,
        accountId,
      };
    }

    set(state => ({
      brokers: state.brokers.map(b =>
        b.id === brokerId
          ? { ...b, accountBalances: normalized, lastUpdated: Date.now() }
          : b
      ),
    }));
  },

  // ============================================================================
  // BROKER ACTIONS
  // ============================================================================

  setActiveBroker: (brokerId) => {
    if (isHiddenBrokerId(brokerId)) return;
    set({ activeBrokerId: brokerId });
  },

  setLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error, isLoading: false }),

  fetchBrokers: () => {
    set({ isLoading: true });
    wsService.emit('broker:list', {});
  },

  connectBroker: async (brokerId) => {
    set({ isLoading: true });

    const result = await createTimeoutPromise<Result & { broker?: BrokerState }>(
      'broker:connect',
      { brokerId },
      'broker:connected',
      'broker:error',
      CONNECT_TIMEOUT,
      (data) => data.brokerId === brokerId
    );

    if (result.success && result.broker) {
      get().updateBroker(brokerId, {
        ...result.broker,
        positions: [],
        // Blanking positions invalidates any snapshot that came before: this
        // may be a different account, and the position may have moved while we
        // were away. Leaving the flag set would read the empty array as a
        // confident "flat".
        positionsLoadedAt: undefined,
        orders: [],
        balance: null,
      });

      // Set as active if no active broker
      if (!get().activeBrokerId) {
        set({ activeBrokerId: brokerId });
      }
    }

    set({ isLoading: false });
    return { success: result.success, error: result.error };
  },

  disconnectBroker: async (brokerId) => {
    const result = await createTimeoutPromise<Result>(
      'broker:disconnect',
      { brokerId },
      'broker:disconnected',
      'broker:error',
      REQUEST_TIMEOUT,
      (data) => data.brokerId === brokerId
    );

    if (result.success) {
      get().updateBroker(brokerId, {
        status: 'disconnected',
        accounts: [],
        selectedAccountId: null,
        positions: [],
        // See connectBroker: the snapshot dies with the connection.
        positionsLoadedAt: undefined,
        orders: [],
        balance: null,
      });

      // If this was active broker, switch to another connected broker
      if (get().activeBrokerId === brokerId) {
        const connected = get().getConnectedBrokers();
        set({ activeBrokerId: connected[0]?.id || null });
      }
    }

    return result;
  },

  saveBrokerCredentials: async (brokerId, username, password) => {
    return createTimeoutPromise<Result>(
      'broker:save-credentials',
      { brokerId, username, password },
      'broker:credentials-saved',
      'broker:error',
      REQUEST_TIMEOUT,
      (data) => data.brokerId === brokerId
    );
  },

  selectBrokerAccount: (brokerId, accountId) => {
    // Global selection: deselect ALL accounts on ALL brokers, then select this one
    const currentBrokers = get().brokers;
    const updatedBrokers = currentBrokers.map(b => ({
      ...b,
      selectedAccountId: b.id === brokerId ? accountId : null, // Only this broker gets the selection
    }));
    set({ brokers: updatedBrokers });

    // Notify backend (backend handles persistence to Redis)
    wsService.emit('broker:select-account', { brokerId, accountId });

    // Also set this as the active broker for trading
    set({ activeBrokerId: brokerId });

    // Toast notification
    const broker = get().getBroker(brokerId);
    const account = broker?.accounts.find(a => a.id === accountId);
    const accountName = account?.name || `#${accountId}`;
    useToastStore.getState().addToast({
      type: 'success',
      message: `Switched to ${accountName}`,
      duration: 2000
    });
  },

  setBrokerEnabled: async (brokerId, enabled) => {
    const result = await createTimeoutPromise<Result>(
      'broker:set-enabled',
      { brokerId, enabled },
      'broker:enabled-changed',
      'broker:error',
      REQUEST_TIMEOUT,
      (data) => data.brokerId === brokerId
    );

    if (result.success) {
      get().updateBroker(brokerId, { enabled });
    }

    return result;
  },

  setBrokerAutoConnect: async (brokerId, autoConnect) => {
    const result = await createTimeoutPromise<Result>(
      'broker:set-auto-connect',
      { brokerId, autoConnect },
      'broker:auto-connect-changed',
      'broker:error',
      REQUEST_TIMEOUT,
      (data) => data.brokerId === brokerId
    );

    if (result.success) {
      get().updateBroker(brokerId, { autoConnect });
    }

    return result;
  },

  setAccountEnabled: async (brokerId, accountId, enabled) => {
    const state = get();

    // VALIDATION: Prevent disabling the currently selected account
    if (!enabled) {
      const broker = state.getBroker(brokerId);
      if (broker && broker.selectedAccountId === accountId) {
        useToastStore.getState().addToast({
          type: 'error',
          message: 'Cannot disable the currently selected account. Please select a different account first.',
        });
        return;
      }
    }

    const result = await createTimeoutPromise<Result>(
      'broker:set-account-enabled',
      { brokerId, accountId, enabled },
      'broker:account-enabled-changed',
      'broker:error',
      REQUEST_TIMEOUT,
      (data) => data.brokerId === brokerId && data.accountId === accountId
    );

    if (result.success) {
      // Update the specific account's enabled state in the store
      set(state => ({
        brokers: state.brokers.map(b =>
          b.id === brokerId
            ? {
                ...b,
                accounts: b.accounts.map(a =>
                  a.id === accountId ? { ...a, enabled } : a
                ),
                lastUpdated: Date.now()
              }
            : b
        ),
      }));
    } else {
      useToastStore.getState().addToast({
        type: 'error',
        message: result.error || `Failed to ${enabled ? 'enable' : 'disable'} account`,
      });
    }

    return result;
  },

  // ============================================================================
  // TRADING ACTIONS
  // ============================================================================

  placeOrder: async (params) => {
    const state = get();
    const brokerId = params.brokerId || state.activeBrokerId;
    const broker = brokerId ? state.getBroker(brokerId) : null;

    if (!broker || broker.status !== 'connected') {
      useToastStore.getState().addToast({ type: 'error', message: 'No connected broker' });
      return { success: false, error: 'No connected broker' };
    }

    if (state.tradingLocked) {
      useToastStore.getState().addToast({ type: 'warning', message: 'Trading is locked - unlock to place orders' });
      return { success: false, error: 'Trading is locked' };
    }

    if (!useMarketStore.getState().isMarketOpen) {
      useToastStore.getState().addToast({ type: 'warning', message: 'Market is closed' });
      return { success: false, error: 'Market is closed' };
    }

    const accountId = params.accountId || broker.selectedAccountId;
    if (!accountId) {
      useToastStore.getState().addToast({ type: 'error', message: 'No account selected' });
      return { success: false, error: 'No account selected' };
    }

    const restrained = restrainRefusal(broker, accountId, params.symbol, params.action, params.quantity);
    if (restrained) {
      useToastStore.getState().addToast({ type: 'warning', message: restrained });
      return { success: false, error: restrained };
    }

    return createTimeoutPromise<Result>(
      'broker:place-order',
      {
        brokerId: broker.id,
        action: params.action,
        symbol: params.symbol,
        quantity: params.quantity,
        orderType: params.orderType || 'Market',
        price: params.price,
        stopPrice: params.stopPrice,
        accountId,
      },
      'broker:order-placed',
      'broker:error',
      REQUEST_TIMEOUT,
      (data) => data.brokerId === broker.id
    );
  },

  placeBracketOrder: async (params) => {
    const state = get();
    const brokerId = params.brokerId || state.activeBrokerId;
    const broker = brokerId ? state.getBroker(brokerId) : null;

    if (!broker || broker.status !== 'connected') {
      useToastStore.getState().addToast({ type: 'error', message: 'No connected broker' });
      return { success: false, error: 'No connected broker' };
    }

    if (state.tradingLocked) {
      useToastStore.getState().addToast({ type: 'warning', message: 'Trading is locked - unlock to place orders' });
      return { success: false, error: 'Trading is locked' };
    }

    if (!useMarketStore.getState().isMarketOpen) {
      useToastStore.getState().addToast({ type: 'warning', message: 'Market is closed' });
      return { success: false, error: 'Market is closed' };
    }

    const accountId = params.accountId || broker.selectedAccountId;
    if (!accountId) {
      useToastStore.getState().addToast({ type: 'error', message: 'No account selected' });
      return { success: false, error: 'No account selected' };
    }

    const restrained = restrainRefusal(broker, accountId, params.symbol, params.action, params.quantity);
    if (restrained) {
      useToastStore.getState().addToast({ type: 'warning', message: restrained });
      return { success: false, error: restrained };
    }

    return createTimeoutPromise<Result>(
      'broker:place-bracket-order',
      {
        brokerId: broker.id,
        action: params.action,
        symbol: params.symbol,
        quantity: params.quantity,
        orderType: params.orderType,
        price: params.price,
        stopPrice: params.stopPrice,
        profitTargetPoints: params.profitTargetPoints,
        stopLossPoints: params.stopLossPoints,
        accountId,
      },
      'broker:bracket-order-placed',
      'broker:error',
      REQUEST_TIMEOUT,
      (data) => data.brokerId === broker.id
    );
  },

  modifyOrder: (orderId, newPrice, isStopPrice, brokerId, accountId, skipDebounce) => {
    console.log('[BROKER STORE] modifyOrder called:', { orderId, newPrice, isStopPrice, brokerId, skipDebounce });
    const state = get();
    const targetBrokerId = brokerId || state.activeBrokerId;

    if (!targetBrokerId) {
      useToastStore.getState().addToast({ type: 'error', message: 'No broker selected' });
      return;
    }

    const broker = state.getBroker(targetBrokerId);

    if (!broker || broker.status !== 'connected') {
      useToastStore.getState().addToast({ type: 'error', message: 'Broker not connected - please reconnect' });
      return;
    }

    if (state.tradingLocked) {
      useToastStore.getState().addToast({ type: 'warning', message: 'Trading is locked - unlock to modify orders' });
      return;
    }

    // CRITICAL: Check if WebSocket is actually connected before attempting to emit
    if (!wsService.isConnected()) {
      useToastStore.getState().addToast({
        type: 'error',
        message: 'WebSocket disconnected - tap to reconnect',
        duration: 5000
      });
      console.error('[BROKER STORE] Cannot modify order - WebSocket disconnected');
      return;
    }

    if (newPrice <= 0) {
      useToastStore.getState().addToast({ type: 'error', message: 'Invalid price for order modification' });
      return;
    }

    const order = broker.orders.find(o => o.id === orderId);
    if (!order) {
      useToastStore.getState().addToast({ type: 'error', message: 'Order not found - please refresh' });
      return;
    }

    // Track pending modification for toast feedback with from/to prices
    const oldPrice = isStopPrice ? (order.stopPrice || order.price || 0) : (order.price || 0);
    pendingModifications.set(orderId, {
      orderId,
      oldPrice,
      newPrice,
      isStopPrice,
      timestamp: Date.now(),
    });

    // Clean up old pending modifications after timeout
    setTimeout(() => {
      const pending = pendingModifications.get(orderId);
      if (pending && Date.now() - pending.timestamp > PENDING_MOD_TIMEOUT_MS) {
        pendingModifications.delete(orderId);
      }
    }, PENDING_MOD_TIMEOUT_MS + 1000);

    console.log('[BROKER STORE] Emitting broker:modify-order:', {
      brokerId: broker.id,
      orderId,
      newPrice,
      isStopPrice,
      oldPrice,
      accountId: accountId || broker.selectedAccountId,
    });

    wsService.emit('broker:modify-order', {
      brokerId: broker.id,
      orderId,
      newPrice,
      isStopPrice,
      accountId: accountId || broker.selectedAccountId,
      skipDebounce: skipDebounce ?? false,  // Fast path for button operations
    });
  },

  modifyOrderQuantity: (orderId, newQuantity, brokerId, accountId) => {
    const state = get();
    const targetBrokerId = brokerId || state.activeBrokerId;

    if (!targetBrokerId) {
      console.warn('[brokerStore] modifyOrderQuantity: No broker selected');
      useToastStore.getState().addToast({ type: 'error', message: 'No broker selected' });
      return;
    }

    const broker = state.getBroker(targetBrokerId);

    if (!broker || broker.status !== 'connected') {
      console.warn('[brokerStore] modifyOrderQuantity: Broker not connected');
      useToastStore.getState().addToast({ type: 'error', message: 'Broker not connected' });
      return;
    }

    if (state.tradingLocked) {
      console.warn('[brokerStore] modifyOrderQuantity: Trading is locked');
      useToastStore.getState().addToast({ type: 'warning', message: 'Trading is locked - unlock to modify orders' });
      return;
    }

    // Check if WebSocket is connected before emitting
    if (!wsService.isConnected()) {
      useToastStore.getState().addToast({
        type: 'error',
        message: 'WebSocket disconnected - tap to reconnect',
        duration: 5000
      });
      console.error('[brokerStore] modifyOrderQuantity: WebSocket disconnected');
      return;
    }

    if (newQuantity <= 0) {
      console.warn('[brokerStore] modifyOrderQuantity: Invalid quantity', newQuantity);
      useToastStore.getState().addToast({ type: 'error', message: 'Invalid quantity for order modification' });
      return;
    }

    const order = broker.orders.find(o => o.id === orderId);
    if (!order) {
      console.warn('[brokerStore] modifyOrderQuantity: Order not found', orderId);
      useToastStore.getState().addToast({ type: 'error', message: 'Order not found' });
      return;
    }

    console.log('[brokerStore] modifyOrderQuantity: Emitting broker:modify-order-quantity', {
      brokerId: broker.id,
      orderId,
      orderType: order.ordType,
      currentQty: order.orderQty,
      newQuantity,
      accountId: accountId || broker.selectedAccountId,
    });

    // Track pending modification for toast feedback
    pendingModifications.set(orderId, {
      orderId,
      oldQty: order.orderQty,
      newQty: newQuantity,
      orderType: order.ordType,
      timestamp: Date.now(),
    });

    // Clean up old pending modifications
    setTimeout(() => {
      pendingModifications.delete(orderId);
    }, PENDING_MOD_TIMEOUT_MS);

    wsService.emit('broker:modify-order-quantity', {
      brokerId: broker.id,
      orderId,
      newQuantity,
      accountId: accountId || broker.selectedAccountId,
    });
  },

  cancelOrder: (orderId, brokerId, accountId) => {
    const state = get();
    const targetBrokerId = brokerId || state.activeBrokerId;

    if (!targetBrokerId) {
      console.warn('[brokerStore] cancelOrder: No broker selected');
      useToastStore.getState().addToast({ type: 'error', message: 'No broker selected' });
      return;
    }

    const broker = state.getBroker(targetBrokerId);

    if (!broker || broker.status !== 'connected') {
      console.warn('[brokerStore] cancelOrder: Broker not connected');
      useToastStore.getState().addToast({ type: 'error', message: 'Broker not connected' });
      return;
    }

    // CRITICAL: Check if WebSocket is actually connected before attempting to emit
    if (!wsService.isConnected()) {
      useToastStore.getState().addToast({
        type: 'error',
        message: 'WebSocket disconnected - tap to reconnect',
        duration: 5000
      });
      console.error('[BROKER STORE] Cannot cancel order - WebSocket disconnected');
      return;
    }

    // Note: Cancel should work even when trading is locked for safety
    const order = broker.orders.find(o => o.id === orderId);
    if (!order) {
      console.warn('[brokerStore] cancelOrder: Order not found', orderId);
      useToastStore.getState().addToast({ type: 'error', message: 'Order not found' });
      return;
    }

    wsService.emit('broker:cancel-order', {
      brokerId: broker.id,
      orderId,
      accountId: accountId || broker.selectedAccountId,
    });
  },

  splitOrder: async (params) => {
    const state = get();
    const targetBrokerId = params.brokerId || state.activeBrokerId;
    const broker = targetBrokerId ? state.getBroker(targetBrokerId) : null;

    if (!broker || broker.status !== 'connected') {
      useToastStore.getState().addToast({ type: 'error', message: 'No connected broker' });
      return { success: false, error: 'No connected broker' };
    }

    if (state.tradingLocked) {
      useToastStore.getState().addToast({ type: 'warning', message: 'Trading is locked - unlock to split orders' });
      return { success: false, error: 'Trading is locked' };
    }

    if (!wsService.isConnected()) {
      useToastStore.getState().addToast({
        type: 'error',
        message: 'WebSocket disconnected - tap to reconnect',
        duration: 5000
      });
      return { success: false, error: 'WebSocket disconnected' };
    }

    // Find the original order to get its details
    const order = broker.orders.find(o => o.id === params.orderId);
    if (!order) {
      useToastStore.getState().addToast({ type: 'error', message: 'Order not found' });
      return { success: false, error: 'Order not found' };
    }

    console.log('[BROKER STORE] splitOrder called:', {
      ...params,
      originalOrder: {
        id: order.id,
        symbol: order.symbol,
        action: order.action,
        ordType: order.ordType,
      }
    });

    // Show toast when split is initiated
    useToastStore.getState().addToast({
      type: 'info',
      message: `Splitting order: ${params.qty1}@${params.price1} + ${params.qty2}@${params.price2}`,
      duration: 3000
    });

    const result = await createTimeoutPromise<Result>(
      'broker:split-order',
      {
        brokerId: broker.id,
        orderId: params.orderId,
        qty1: params.qty1,
        qty2: params.qty2,
        price1: params.price1,
        price2: params.price2,
        // Pass order details for the backend to create new orders
        symbol: order.symbol,
        action: order.action,
        ordType: order.ordType,
        accountId: params.accountId || broker.selectedAccountId,
      },
      'broker:order-split',
      'broker:error',
      REQUEST_TIMEOUT * 2,  // Longer timeout for multiple operations
      (data) => data.brokerId === broker.id
    );

    // Show result toast
    if (result.success) {
      useToastStore.getState().addToast({
        type: 'success',
        message: 'Order split successfully',
        duration: 3000
      });
    } else {
      useToastStore.getState().addToast({
        type: 'error',
        message: `Split failed: ${result.error || 'Unknown error'}`,
        duration: 5000
      });
    }

    return result;
  },

  replaceBracket: async (params) => {
    const state = get();
    const targetBrokerId = params.brokerId || state.activeBrokerId;
    const broker = targetBrokerId ? state.getBroker(targetBrokerId) : null;

    if (!broker || broker.status !== 'connected') {
      useToastStore.getState().addToast({ type: 'error', message: 'No connected broker' });
      return { success: false, error: 'No connected broker' };
    }

    if (state.tradingLocked) {
      useToastStore.getState().addToast({ type: 'warning', message: 'Trading is locked - unlock to modify orders' });
      return { success: false, error: 'Trading is locked' };
    }

    if (!wsService.isConnected()) {
      useToastStore.getState().addToast({
        type: 'error',
        message: 'WebSocket disconnected - tap to reconnect',
        duration: 5000
      });
      return { success: false, error: 'WebSocket disconnected' };
    }

    console.log('[BROKER STORE] replaceBracket called:', params);

    return createTimeoutPromise<Result>(
      'broker:replace-bracket',
      {
        brokerId: broker.id,
        tpOrderId: params.tpOrderId,
        slOrderId: params.slOrderId,
        symbol: params.symbol,
        action: params.action,
        newQuantity: params.newQuantity,
        tpPrice: params.tpPrice,
        slPrice: params.slPrice,
        accountId: params.accountId || broker.selectedAccountId,
      },
      'broker:bracket-replaced',
      'broker:error',
      REQUEST_TIMEOUT * 2,  // Longer timeout for multiple operations
      (data) => data.brokerId === broker.id
    );
  },

  flattenPosition: async (symbol, brokerId, accountId) => {
    const state = get();
    const broker = state.getBroker(brokerId || state.activeBrokerId || '');

    if (!broker || broker.status !== 'connected') {
      useToastStore.getState().addToast({ type: 'error', message: 'No connected broker' });
      return { success: false, error: 'No connected broker' };
    }

    if (state.tradingLocked) {
      useToastStore.getState().addToast({ type: 'warning', message: 'Trading is locked - unlock to flatten positions' });
      return { success: false, error: 'Trading is locked' };
    }

    // CRITICAL: Check if WebSocket is actually connected before attempting to emit
    if (!wsService.isConnected()) {
      useToastStore.getState().addToast({
        type: 'error',
        message: 'WebSocket disconnected - tap to reconnect',
        duration: 5000
      });
      console.error('[BROKER STORE] Cannot flatten position - WebSocket disconnected');
      return { success: false, error: 'WebSocket disconnected' };
    }

    if (!useMarketStore.getState().isMarketOpen) {
      useToastStore.getState().addToast({ type: 'warning', message: 'Market is closed' });
      return { success: false, error: 'Market is closed' };
    }

    return createTimeoutPromise<Result>(
      'broker:flatten',
      {
        brokerId: broker.id,
        symbol,
        accountId: accountId || broker.selectedAccountId,
      },
      'broker:position-flattened',
      'broker:error',
      REQUEST_TIMEOUT,
      (data) => data.brokerId === broker.id
    );
  },

  flattenAllPositions: async (symbol) => {
    const connectedBrokers = get().getConnectedBrokers();
    const results = await Promise.all(
      connectedBrokers.map(async broker => {
        try {
          return await get().flattenPosition(symbol, broker.id, broker.selectedAccountId || undefined);
        } catch (error) {
          console.error(`[brokerStore] flattenAllPositions: Error flattening ${broker.id}:`, error);
          return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
        }
      })
    );

    const failed = results.filter(r => !r.success);
    if (failed.length > 0) {
      return { success: false, error: `${failed.length} broker(s) failed to flatten` };
    }

    return { success: true };
  },

  fetchPositions: (brokerId) => {
    const state = get();
    const broker = state.getBroker(brokerId || state.activeBrokerId || '');

    if (!broker || broker.status !== 'connected') return;

    wsService.emit('broker:get-positions', {
      brokerId: broker.id,
      accountId: broker.selectedAccountId,
    });
  },

  fetchOrders: (brokerId) => {
    const state = get();
    const broker = state.getBroker(brokerId || state.activeBrokerId || '');

    if (!broker || broker.status !== 'connected') return;

    wsService.emit('broker:get-orders', {
      brokerId: broker.id,
      accountId: broker.selectedAccountId,
    });
  },

  fetchAccountBalance: (brokerId) => {
    const state = get();
    const broker = state.getBroker(brokerId || state.activeBrokerId || '');

    if (!broker || broker.status !== 'connected') return;

    wsService.emit('broker:get-balance', {
      brokerId: broker.id,
      accountId: broker.selectedAccountId,
    });
  },

  // ============================================================================
  // RMS ACTIONS
  // ============================================================================

  setTradingLocked: (locked, lockedAt, reason) => set({
    tradingLocked: locked,
    tradingLockedAt: lockedAt ?? null,
    tradingLockedReason: reason ?? null,
  }),

  setTradingLock: async (locked, reason) => {
    // Store previous state for rollback on failure
    const previousState = {
      tradingLocked: get().tradingLocked,
      tradingLockedAt: get().tradingLockedAt,
      tradingLockedReason: get().tradingLockedReason,
    };

    // Optimistic local update for immediate UI feedback
    set({
      tradingLocked: locked,
      tradingLockedAt: locked ? new Date().toISOString() : null,
      tradingLockedReason: reason ?? null,
    });

    // Emit to server for persistence with timeout/error handling
    const result = await createTimeoutPromise<Result>(
      'rms:set-trading-lock',
      { locked, reason },
      'rms:status', // Server responds with updated status
      'rms:error',
      REQUEST_TIMEOUT
    );

    if (!result.success) {
      // Rollback to previous state on failure
      console.warn('[BROKER STORE] setTradingLock failed, rolling back:', result.error);
      set(previousState);
      useToastStore.getState().addToast({
        type: 'error',
        message: `Failed to ${locked ? 'lock' : 'unlock'} trading: ${result.error}`,
      });
    }
    return result;
  },

  fetchRMSStatus: () => {
    wsService.emit('rms:get-status');
  },

  // ============================================================================
  // COPY TRADING ACTIONS
  // ============================================================================

  setCopyTradingConfig: (config) => set({ copyTradingConfig: config }),

  setCopyTradingEnabled: (enabled) => set({ copyTradingEnabled: enabled }),

  addCopyTradeEvent: (event) => set(state => ({
    copyTradingHistory: [event, ...state.copyTradingHistory].slice(0, 100),
  })),

  setCopyTradingHistory: (history) => set({ copyTradingHistory: history }),

  addCopyTradeActivity: (activity) => set(state => ({
    copyTradeActivities: [activity, ...state.copyTradeActivities].slice(0, 200),
  })),

  clearCopyTradeActivities: () => set({ copyTradeActivities: [] }),

  fetchCopyTradingStatus: () => {
    wsService.emit('copytrade:get-status', {});
  },

  updateCopyTradingConfig: async (config) => {
    const result = await createTimeoutPromise<Result>(
      'copytrade:set-config',
      { config },
      'copytrade:config-updated',
      'copytrade:error',
      REQUEST_TIMEOUT
    );

    if (result.success) {
      set({ copyTradingConfig: config });
    }

    return result;
  },

  enableCopyTrading: async () => {
    const result = await createTimeoutPromise<Result>(
      'copytrade:enable',
      {},
      'copytrade:enabled',
      'copytrade:error',
      REQUEST_TIMEOUT
    );

    if (result.success) {
      set({ copyTradingEnabled: true });
    }

    return result;
  },

  disableCopyTrading: async () => {
    const result = await createTimeoutPromise<Result>(
      'copytrade:disable',
      {},
      'copytrade:disabled',
      'copytrade:error',
      REQUEST_TIMEOUT
    );

    if (result.success) {
      set({ copyTradingEnabled: false });
    }

    return result;
  },

  fetchCopyTradingHistory: () => {
    wsService.emit('copytrade:get-history', {});
  },
}))
);

// ============================================================================
// WEBSOCKET EVENT HANDLERS
// ============================================================================

// Guard to prevent duplicate event listener registration on reconnects
let brokerSocketHandlersInitialized = false;
// Generation counter to detect stale handlers
let handlerGeneration = 0;

/**
 * Reset the broker socket handlers initialization flag.
 * MUST be called before forceReconnect() to ensure handlers are re-registered on new socket.
 * Without this, broker:positions, broker:orders, etc. events won't be received after reconnection.
 */
export function resetBrokerSocketHandlers() {
  brokerSocketHandlersInitialized = false;
  handlerGeneration++; // Increment generation to invalidate old handlers
  console.log('[BROKER STORE] Reset socket handlers flag - generation:', handlerGeneration);
}

export function initBrokerSocketHandlers() {
  const store = useBrokerStore.getState;

  // Always request fresh broker list on connect/reconnect
  setTimeout(() => {
    store().fetchBrokers();
    console.log('[BROKER STORE] Requested fresh broker list on connect');
  }, 100);

  // Prevent duplicate registration (critical: Socket.IO appends listeners, doesn't replace)
  if (brokerSocketHandlersInitialized) {
    console.log('[BROKER STORE] Socket handlers already initialized, fetched fresh data');
    return;
  }
  brokerSocketHandlersInitialized = true;

  // ============================================================================
  // BROKER LIFECYCLE EVENTS
  // ============================================================================

  wsService.on('broker:list', (data: { brokers: BrokerState[] }) => {
    console.log('[BROKER STORE] Received broker:list with', data.brokers?.length, 'brokers');
    data.brokers?.forEach(b => {
      if (b.selectedAccountId) {
        console.log(`[BROKER STORE] Broker ${b.id} has selectedAccountId: ${b.selectedAccountId}`);
      }
    });
    store().setBrokers(data.brokers || []);
  });

  wsService.on('broker:status', (data: { brokerId: string; status: BrokerState['status']; error?: string }) => {
    store().updateBrokerStatus(data.brokerId, data.status, data.error);
  });

  wsService.on('broker:connected', (data: { brokerId: string; broker: BrokerState }) => {
    if (isHiddenBrokerId(data.brokerId)) return;
    if (!isFuturesBroker(data.broker)) {
      filterFuturesBrokers([{ ...data.broker, id: data.brokerId }]);
      return;
    }
    // Preserve existing positions/orders if they exist - they might have arrived
    // via broker:list or broker:orders before this event
    const existingBroker = store().getBroker(data.brokerId);
    const preservedPositions = existingBroker?.positions?.length ? existingBroker.positions : [];
    const preservedOrders = existingBroker?.orders?.length ? existingBroker.orders : [];
    const preservedBalance = existingBroker?.balance || null;

    store().updateBroker(data.brokerId, {
      ...data.broker,
      status: 'connected',
      connectedAt: Date.now(),
      error: undefined,
      positions: preservedPositions,
      orders: preservedOrders,
      balance: preservedBalance,
    });

    // Set as active if no active broker
    if (!store().activeBrokerId) {
      console.log('[BROKER STORE] broker:connected - setting activeBrokerId to', data.brokerId);
      store().setActiveBroker(data.brokerId);
    } else {
      console.log('[BROKER STORE] broker:connected - activeBrokerId already set to', store().activeBrokerId);
    }
  });

  wsService.on('broker:disconnected', (data: { brokerId: string }) => {
    store().updateBroker(data.brokerId, {
      status: 'disconnected',
      accounts: [],
      selectedAccountId: null,
      positions: [],
      // The engine drops its cached broker_positions on this event too
      // (relay/mod.rs), so nothing will re-assert them until a reconnect.
      positionsLoadedAt: undefined,
      orders: [],
      balance: null,
    });

    // Clear debounce buffers for disconnected broker to prevent stale data on reconnection
    positionBuffer.delete(data.brokerId);
    orderBuffer.delete(data.brokerId);
    balanceBuffer.delete(data.brokerId);

    // Clear timeout if all buffers are now empty
    if (positionBuffer.size === 0 && orderBuffer.size === 0 && balanceBuffer.size === 0 && flushTimeout) {
      clearTimeout(flushTimeout);
      flushTimeout = null;
    }
  });

  wsService.on('broker:accounts', (data: { brokerId: string; accounts: BrokerAccount[] }) => {
    store().setBrokerAccounts(data.brokerId, data.accounts);
  });

  wsService.on('broker:account-selected', (data: { brokerId: string; accountId: number }) => {
    console.log('[BROKER STORE] Received broker:account-selected:', data);
    store().updateBroker(data.brokerId, { selectedAccountId: data.accountId });
  });

  // ============================================================================
  // TRADING DATA EVENTS (real-time updates with debouncing for performance)
  // ============================================================================

  // Debounce buffers for high-frequency updates
  const positionBuffer = new Map<string, any[]>();
  const orderBuffer = new Map<string, any[]>();
  const balanceBuffer = new Map<string, any>();
  let flushTimeout: ReturnType<typeof setTimeout> | null = null;
  const DEBOUNCE_MS = 100; // 100ms debounce for rapid updates
  const MAX_BUFFER_ENTRIES = 50; // Maximum brokers to buffer before force flush

  const flushBuffers = () => {
    // Flush positions - validate data before processing
    positionBuffer.forEach((positions, brokerId) => {
      // Skip if broker disconnected or data is invalid
      const broker = store().getBroker(brokerId);
      if (!broker || broker.status !== 'connected') {
        console.log('[BROKER STORE] Skipping flush for disconnected broker:', brokerId);
        return;
      }
      if (!positions || !Array.isArray(positions)) {
        console.warn('[BROKER STORE] Invalid positions data for broker:', brokerId);
        return;
      }
      console.log('[BROKER STORE] Flushing positions', brokerId, positions.length);
      store().updateBrokerPositions(brokerId, positions);
    });
    positionBuffer.clear();

    // Flush orders - validate data before processing
    orderBuffer.forEach((orders, brokerId) => {
      const broker = store().getBroker(brokerId);
      if (!broker || broker.status !== 'connected') return;
      if (!orders || !Array.isArray(orders)) return;
      store().updateBrokerOrders(brokerId, orders);
    });
    orderBuffer.clear();

    // Flush balances - validate data before processing
    balanceBuffer.forEach((balance, brokerId) => {
      const broker = store().getBroker(brokerId);
      if (!broker || broker.status !== 'connected') return;
      if (!balance || typeof balance !== 'object') return;
      store().updateBrokerBalance(brokerId, balance);
    });
    balanceBuffer.clear();

    flushTimeout = null;
  };

  const scheduleFlush = () => {
    // Force immediate flush if buffers are getting too large (prevent memory issues)
    const totalEntries = positionBuffer.size + orderBuffer.size + balanceBuffer.size;
    if (totalEntries >= MAX_BUFFER_ENTRIES) {
      if (flushTimeout) {
        clearTimeout(flushTimeout);
        flushTimeout = null;
      }
      flushBuffers();
      return;
    }

    if (!flushTimeout) {
      flushTimeout = setTimeout(flushBuffers, DEBOUNCE_MS);
    }
  };

  wsService.on('broker:positions', (data: { brokerId: string; positions: any[] }) => {
    console.log('[BROKER STORE] Received broker:positions', data.brokerId, data.positions?.length || 0);
    if (data.positions) {
      positionBuffer.set(data.brokerId, data.positions);
      scheduleFlush();
    }
  });

  wsService.on('broker:orders', (data: { brokerId: string; orders: any[] }) => {
    if (data.orders) {
      orderBuffer.set(data.brokerId, data.orders);
      scheduleFlush();
    }
  });

  // Handle immediate order price updates (no race condition with cache)
  wsService.on('broker:order-price-updated', (data: { brokerId: string; orderId: number; newPrice: number; isStopPrice: boolean }) => {
    console.log(`[BROKER STORE] Immediate price update for order ${data.orderId}: ${data.newPrice} (isStop: ${data.isStopPrice})`);
    const broker = store().getBroker(data.brokerId);
    if (broker && broker.status === 'connected') {
      // Update just this order's price in place
      const updatedOrders = broker.orders.map(order => {
        if (order.id === data.orderId) {
          return {
            ...order,
            price: data.isStopPrice ? order.price : data.newPrice,
            stopPrice: data.isStopPrice ? data.newPrice : order.stopPrice,
          };
        }
        return order;
      });
      store().updateBrokerOrders(data.brokerId, updatedOrders);
    }
  });

  // Handle order modification acknowledgement - show success toast with from/to prices or quantities
  wsService.on('broker:order-modified', (data: { brokerId: string; success: boolean; orderId?: number; error?: string }) => {
    if (data.success && data.orderId) {
      console.log(`[BROKER STORE] Order modified successfully:`, data);

      // Look up pending modification for details
      const pending = pendingModifications.get(data.orderId);
      let message = 'Order modified';

      if (pending) {
        if (pending.oldQty !== undefined && pending.newQty !== undefined) {
          // Quantity modification
          const orderType = pending.orderType || 'Order';
          message = `${orderType} qty: ${pending.oldQty} → ${pending.newQty}`;
        } else if (pending.oldPrice !== undefined && pending.newPrice !== undefined) {
          // Price modification
          const fromPrice = pending.oldPrice.toFixed(2);
          const toPrice = pending.newPrice.toFixed(2);
          const priceType = pending.isStopPrice ? 'Stop' : 'Limit';
          message = `${priceType} moved: ${fromPrice} → ${toPrice}`;
        }
        pendingModifications.delete(data.orderId);
      }

      useToastStore.getState().addToast({
        type: 'success',
        message,
        duration: 2000
      });
    } else if (data.success) {
      // Success but no orderId (shouldn't happen, but handle gracefully)
      useToastStore.getState().addToast({
        type: 'success',
        message: 'Order modified',
        duration: 2000
      });
    } else {
      console.error(`[BROKER STORE] Order modification failed:`, data);

      // Clean up pending modification on failure too
      if (data.orderId) {
        pendingModifications.delete(data.orderId);
      }

      useToastStore.getState().addToast({
        type: 'error',
        message: data.error || 'Failed to modify order',
        duration: 3000
      });
    }
  });

  wsService.on('broker:balance', (data: { brokerId: string; balance: any }) => {
    if (data.balance) {
      balanceBuffer.set(data.brokerId, data.balance);
      scheduleFlush();
    }
  });

  // Handle broker errors - show toast to user so they know something failed
  wsService.on('broker:error', (data: { brokerId: string; message: string; code?: string }) => {
    if (isHiddenBrokerId(data.brokerId)) return;
    console.error('[BROKER ERROR]', data);
    const broker = store().getBroker(data.brokerId);
    const brokerName = broker?.name || data.brokerId;

    // Show user-friendly error message
    let message = data.message || 'Unknown error';
    if (data.code === 'MODIFY_ORDER_FAILED') {
      message = `Failed to modify order: ${data.message}`;
    } else if (data.message?.includes('Not connected')) {
      message = `${brokerName} is disconnected. Please reconnect.`;
      // Update broker status to reflect disconnection
      store().updateBroker(data.brokerId, { status: 'error', error: 'Session expired' });
    }

    useToastStore.getState().addToast({ type: 'error', message });
  });

  // ============================================================================
  // RMS EVENTS (global, not broker-specific)
  // ============================================================================

  wsService.on('rms:status', (data: {
    tradingLocked: boolean;
    lockedAt: string | null;
    lockedReason: string | null;
  }) => {
    store().setTradingLocked(data.tradingLocked, data.lockedAt, data.lockedReason);
  });

  // ============================================================================
  // COPY TRADING EVENTS
  // ============================================================================

  wsService.on('copytrade:status', (data: {
    enabled: boolean;
    config: CopyTradingConfig | null;
  }) => {
    store().setCopyTradingEnabled(data.enabled);
    store().setCopyTradingConfig(data.config);
  });

  wsService.on('copytrade:config', (data: { config: CopyTradingConfig }) => {
    store().setCopyTradingConfig(data.config);
  });

  wsService.on('copytrade:executed', (data: CopyTradeEvent) => {
    store().addCopyTradeEvent(data);
  });

  wsService.on('copytrade:history', (data: { history: CopyTradeEvent[] }) => {
    store().setCopyTradingHistory(data.history);
  });

  wsService.on('copytrade:activity', (data: CopyTradeActivity) => {
    store().addCopyTradeActivity(data);
  });

  // NOTE: Initial fetches are done in WebSocketService.ts socket 'connect' event
  // to ensure socket is connected before emitting
}

// ============================================================================
// SELECTORS (for optimized subscriptions)
// ============================================================================

// =============================================================================
// SELECTORS - Select raw state, components use useMemo to derive values
// =============================================================================

// Stable selector functions (defined outside component scope)
const selectBrokers = (state: BrokerStoreState) => state.brokers;
const selectActiveBrokerId = (state: BrokerStoreState) => state.activeBrokerId;
const selectTradingLocked = (state: BrokerStoreState) => state.tradingLocked;
const selectTradingLockedAt = (state: BrokerStoreState) => state.tradingLockedAt;
const selectTradingLockedReason = (state: BrokerStoreState) => state.tradingLockedReason;

export const brokerSelectors = {
  // Base state selectors - return stable references
  useBrokers: () => useBrokerStore(selectBrokers),
  useActiveBrokerId: () => useBrokerStore(selectActiveBrokerId),
  useTradingLocked: () => useBrokerStore(selectTradingLocked),
  useTradingLockedAt: () => useBrokerStore(selectTradingLockedAt),
  useTradingLockedReason: () => useBrokerStore(selectTradingLockedReason),

  // Getter-based selectors
  useActiveBroker: () => useBrokerStore(state => state.getActiveBroker()),
  useBroker: (brokerId: string) => useBrokerStore(state => state.getBroker(brokerId)),
  useBrokerBalance: (brokerId: string) => useBrokerStore(state =>
    state.getBroker(brokerId)?.balance
  ),
};

/**
 * Get orders for the active broker - properly reactive to store changes
 * Use this instead of activeBroker?.orders to ensure re-renders on order updates
 */
export function useActiveBrokerOrders(): BrokerOrder[] {
  const brokers = useBrokerStore(selectBrokers);
  const activeBrokerId = useBrokerStore(selectActiveBrokerId);
  return useMemo(() => {
    const broker = brokers.find(b => b.id === activeBrokerId);
    return broker?.orders ?? [];
  }, [brokers, activeBrokerId]);
}

/**
 * Get positions for the active broker - properly reactive to store changes
 * Use this instead of activeBroker?.positions to ensure re-renders on position updates
 */
export function useActiveBrokerPositions(): BrokerPosition[] {
  const brokers = useBrokerStore(selectBrokers);
  const activeBrokerId = useBrokerStore(selectActiveBrokerId);
  return useMemo(() => {
    const broker = brokers.find(b => b.id === activeBrokerId);
    return broker?.positions ?? [];
  }, [brokers, activeBrokerId]);
}

// =============================================================================
// DERIVED HOOKS - Properly memoized derived values
// Use these instead of computing values inline
// =============================================================================

export function useConnectedBrokers() {
  const brokers = useBrokerStore(selectBrokers);
  return useMemo(() => brokers.filter(b => b.status === 'connected'), [brokers]);
}

export function useAllPositions() {
  const brokers = useBrokerStore(selectBrokers);
  return useMemo(() => {
    const positions: BrokerPosition[] = [];
    brokers
      .filter(b => b.status === 'connected')
      .forEach(b => {
        // Get enabled account IDs
        const enabledAccountIds = new Set(
          b.accounts.filter(a => a.enabled !== false).map(a => a.id)
        );
        // Only include positions from enabled accounts
        b.positions.forEach(p => {
          if (enabledAccountIds.has(p.accountId)) {
            positions.push(p);
          }
        });
      });
    return positions;
  }, [brokers]);
}

export function useAllOrders() {
  const brokers = useBrokerStore(selectBrokers);
  return useMemo(() => {
    const orders: BrokerOrder[] = [];
    brokers
      .filter(b => b.status === 'connected')
      .forEach(b => {
        // Get enabled account IDs
        const enabledAccountIds = new Set(
          b.accounts.filter(a => a.enabled !== false).map(a => a.id)
        );
        // Only include orders from enabled accounts
        b.orders.forEach(o => {
          if (enabledAccountIds.has(o.accountId)) {
            orders.push(o);
          }
        });
      });
    return orders;
  }, [brokers]);
}

// Returns only ENABLED accounts (for dropdowns, trading, etc.)
export function useAllAccounts() {
  const brokers = useBrokerStore(selectBrokers);
  return useMemo(() =>
    brokers
      .filter(b => b.status === 'connected')
      .flatMap(b => b.accounts
        .filter(a => a.enabled !== false) // Only enabled accounts
        .map(a => ({
          ...a,
          brokerId: b.id,
          brokerName: b.name,
        }))
      ),
    [brokers]
  );
}

// Returns ALL accounts including disabled (for management UI)
export function useAllAccountsUnfiltered() {
  const brokers = useBrokerStore(selectBrokers);
  return useMemo(() =>
    brokers
      .filter(b => b.status === 'connected')
      .flatMap(b => b.accounts.map(a => ({
        ...a,
        brokerId: b.id,
        brokerName: b.name,
      }))),
    [brokers]
  );
}

export function useAggregatedBalance() {
  const brokers = useBrokerStore(selectBrokers);
  return useMemo(() => {
    const brokerBalances: BrokerBalance[] = [];
    brokers.forEach(broker => {
      if (broker.status === 'connected' && broker.balance) {
        brokerBalances.push(broker.balance);
      }
    });
    const netLiq = brokerBalances.reduce((sum, b) => sum + b.netLiq, 0);
    const openPnL = brokerBalances.reduce((sum, b) => sum + b.openPnL, 0);
    const realizedPnL = brokerBalances.reduce((sum, b) => sum + b.realizedPnL, 0);
    const cashBalance = brokerBalances.reduce((sum, b) => sum + b.cashBalance, 0);
    const marginUsed = brokerBalances.reduce((sum, b) => sum + (b.marginUsed || 0), 0);
    return {
      netLiq,
      openPnL,
      realizedPnL,
      totalPnL: openPnL + realizedPnL,
      cashBalance,
      marginUsed,
      brokerBalances,
    };
  }, [brokers]);
}

/**
 * Get balance for the active broker's selected account only
 * Used for document title PnL display
 */
export function useActiveBrokerBalance() {
  const activeBroker = useBrokerStore(state => state.getActiveBroker());
  return useMemo(() => {
    if (!activeBroker || activeBroker.status !== 'connected') {
      return { realizedPnL: 0, openPnL: 0, totalPnL: 0, netLiq: 0 };
    }

    // If there's a selected account and we have per-account balances, use that
    const selectedAccountId = activeBroker.selectedAccountId;
    if (selectedAccountId && activeBroker.accountBalances?.[selectedAccountId]) {
      const balance = activeBroker.accountBalances[selectedAccountId];
      return {
        realizedPnL: balance.realizedPnL || 0,
        openPnL: balance.openPnL || 0,
        totalPnL: (balance.realizedPnL || 0) + (balance.openPnL || 0),
        netLiq: balance.netLiq || 0,
      };
    }

    // Fallback to broker's main balance (which should be for selected account)
    if (activeBroker.balance) {
      return {
        realizedPnL: activeBroker.balance.realizedPnL || 0,
        openPnL: activeBroker.balance.openPnL || 0,
        totalPnL: (activeBroker.balance.realizedPnL || 0) + (activeBroker.balance.openPnL || 0),
        netLiq: activeBroker.balance.netLiq || 0,
      };
    }

    return { realizedPnL: 0, openPnL: 0, totalPnL: 0, netLiq: 0 };
  }, [activeBroker]);
}

export function useAggregatedPositions() {
  const brokers = useBrokerStore(selectBrokers);
  return useMemo(() => {
    const positions: BrokerPosition[] = [];
    brokers.forEach(broker => {
      if (broker.status === 'connected') {
        positions.push(...broker.positions);
      }
    });
    const bySymbol = new Map<string, BrokerPosition[]>();
    positions.forEach(pos => {
      const symbol = pos.symbol || `Contract#${pos.contractId}`;
      const existing = bySymbol.get(symbol) || [];
      existing.push(pos);
      bySymbol.set(symbol, existing);
    });
    return Array.from(bySymbol.entries()).map(([symbol, brokerPositions]) => {
      const totalNetPos = brokerPositions.reduce((sum, p) => sum + p.netPos, 0);
      const totalValue = brokerPositions.reduce((sum, p) => sum + Math.abs(p.netPos) * p.netPrice, 0);
      const totalQty = brokerPositions.reduce((sum, p) => sum + Math.abs(p.netPos), 0);
      return {
        symbol,
        totalNetPos,
        avgEntryPrice: totalQty > 0 ? totalValue / totalQty : 0,
        totalOpenPnL: brokerPositions.reduce((sum, p) => sum + (p.openPnL || 0), 0),
        brokerPositions,
      };
    });
  }, [brokers]);
}
